/**
 * Pipeline Stage CRUD — spec §3.3 projects/{projectId}/pipeline/{stageId}
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  onSnapshot,
  updateDoc,
  query,
  where,
  serverTimestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { PipelineStage, PipelineStageName, StageStatus, VerificationResult, ArtifactRef } from "@/types";

function pipelineRef(projectId: string) {
  return collection(db, "projects", projectId, "pipeline");
}

export async function createPipelineStage(
  projectId: string,
  stage: PipelineStageName,
): Promise<string> {
  const docRef = await addDoc(pipelineRef(projectId), {
    project_id: projectId,
    stage,
    status: "pending" as StageStatus,
    artifact_refs: [],
    verification_results: [],
    retry_count: 0,
    cost_usd: 0,
  });
  return docRef.id;
}

export async function getPipelineStage(
  projectId: string,
  stageId: string,
): Promise<PipelineStage | null> {
  const snap = await getDoc(doc(db, "projects", projectId, "pipeline", stageId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as PipelineStage;
}

export async function getPipelineStageByName(
  projectId: string,
  stageName: PipelineStageName,
): Promise<PipelineStage | null> {
  const q = query(pipelineRef(projectId), where("stage", "==", stageName));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as PipelineStage;
}

export function subscribePipelineStages(
  projectId: string,
  onData: (stages: PipelineStage[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    pipelineRef(projectId),
    (snap) => {
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PipelineStage));
    },
    onError,
  );
}

export async function updatePipelineStageStatus(
  projectId: string,
  stageId: string,
  status: StageStatus,
  errorMessage?: string,
): Promise<void> {
  const updates: Record<string, unknown> = { status };
  if (status === "running") updates.started_at = serverTimestamp();
  if (status === "completed" || status === "failed") updates.completed_at = serverTimestamp();
  if (errorMessage) updates.error_message = errorMessage;
  await updateDoc(doc(db, "projects", projectId, "pipeline", stageId), updates);
}

export async function addVerificationResult(
  projectId: string,
  stageId: string,
  result: VerificationResult,
): Promise<void> {
  const stage = await getPipelineStage(projectId, stageId);
  if (!stage) throw new Error(`Pipeline stage ${stageId} not found`);
  await updateDoc(doc(db, "projects", projectId, "pipeline", stageId), {
    verification_results: [...stage.verification_results, result],
  });
}

export async function addArtifactRef(
  projectId: string,
  stageId: string,
  artifact: ArtifactRef,
): Promise<void> {
  const stage = await getPipelineStage(projectId, stageId);
  if (!stage) throw new Error(`Pipeline stage ${stageId} not found`);
  await updateDoc(doc(db, "projects", projectId, "pipeline", stageId), {
    artifact_refs: [...stage.artifact_refs, artifact],
  });
}

export async function updateStageCost(
  projectId: string,
  stageId: string,
  costUsd: number,
): Promise<void> {
  await updateDoc(doc(db, "projects", projectId, "pipeline", stageId), {
    cost_usd: costUsd,
  });
}

export async function incrementRetryCount(
  projectId: string,
  stageId: string,
): Promise<void> {
  const stage = await getPipelineStage(projectId, stageId);
  if (!stage) throw new Error(`Pipeline stage ${stageId} not found`);
  await updateDoc(doc(db, "projects", projectId, "pipeline", stageId), {
    retry_count: stage.retry_count + 1,
    status: "retrying" as StageStatus,
  });
}
